import type {
  CrossDeviceAuthStartResponse,
  PairingStartResponse,
  PairingStatusResponse,
} from './types';
import { BaseBackendServiceHttp } from './BaseBackendServiceHttp';

export class BaseBackendServicePairing extends BaseBackendServiceHttp {
  sendMessage(subject: string, message: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      'sendEmail',
      'POST',
      // EnquiryEmailController.sendEmail requires variant to be exactly
      // "contact" or "callback" — this is the fund-opportunity "Send a
      // Message" flow's only caller (uiHandlers.tsx submitMessage), always
      // a contact enquiry, never a callback request (that's a separate
      // Google Calendar iframe flow that never hits this endpoint).
      // Omitting it made every call fail with 400 "Invalid variant".
      { message, subject, variant: 'contact' },
      () => undefined,
    );
  }

  startPairingSession(
    destinationType: 'web' | 'app' = 'app',
    destinationDeviceInfo?: { platform?: string; userAgent?: string },
  ): Promise<PairingStartResponse> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL('auth/pairing/start'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ destinationType, destinationDeviceInfo }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }
      return (await response.json()) as PairingStartResponse;
    });
  }

  getPairingStatus(pollingToken: string): Promise<PairingStatusResponse> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(
        this.apiURL(`auth/pairing/status/${pollingToken}`),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }
      return (await response.json()) as PairingStatusResponse;
    });
  }

  approvePairing(
    pairingId: string,
    originDeviceInfo?: { platform?: string; userAgent?: string },
  ): Promise<{ correctChoice: string }> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL('auth/pairing/approve'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ pairingId, originDeviceInfo }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }
      return (await response.json()) as { correctChoice: string };
    });
  }

  startCrossDeviceAuth(
    destinationType: 'web' | 'mobile' = 'web',
    destinationDeviceInfo?: { platform?: string; userAgent?: string },
  ): Promise<CrossDeviceAuthStartResponse> {
    const actualDestType = destinationType === 'mobile' ? 'app' : 'web';

    return this.startPairingSession(actualDestType, destinationDeviceInfo).then(
      (pairingResponse) => ({
        pairingId: pairingResponse.pairingId,
        ticketUrl: `${window.location.origin}/auth/transfer?pairingId=${pairingResponse.pairingId}`,
        shortCode: this.generateShortCode(pairingResponse.pairingId),
        expiresAt: pairingResponse.expiresAt,
        qrData: pairingResponse.qrData,
      }),
    );
  }

  private generateShortCode(pairingId: string): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    return pairingId
      .slice(-6)
      .toUpperCase()
      .split('')
      .map((c) => chars[parseInt(c, 36) % chars.length] || chars[0])
      .join('');
  }

  generateContentPDF(
    modelType: string,
    modelId: string,
    draft: boolean,
    downloadFileName: string,
  ): Promise<{ jobId: string }> {
    return this.authApiMutateAuthed(
      'GeneratePDF',
      'POST',
      { modelType, modelId, draft, downloadFileName },
      (json) => json as { jobId: string },
    );
  }

  checkPDFJob(jobId: string) {
    return this.authApiGetAuthed(
      `CheckPDFJob/${jobId}`,
      'Network error while checking PDF job',
      (json) => json as { jobId: string; status: 'PENDING' | 'COMPLETE' | 'FAILED'; url: string },
    );
  }
}
